/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { FeatureFlagProvider } from "@/contexts/FeatureFlagContext";
import { SessionTimeoutGuard } from "@/components/layout/SessionTimeoutGuard";
import { SupportModeBanner } from "@/components/layout/SupportModeBanner";
import { useGlobalHotkeys } from "@/hooks/useGlobalHotkeys";
import { SolomonProvider } from "@/components/solomon";
import { SupportModeProvider } from "@/hooks/useSupportMode";
import { OrganizationProvider } from "@/hooks/useOrganization";
import { useOptionalTenantContext, TenantContextProvider } from "@/hooks/useTenantContext";

function GlobalHotkeys() {
  useGlobalHotkeys();
  return null;
}

const PUBLIC_AUTH_ROUTES = [
  "/auth",
  "/member/signin",
  "/reset-password",
  "/accept-invite",
  "/recover-account",
  "/403",
  "/account-disabled",
  "/public",
  "/f/",
  "/d/",
  "/register",
];

export function isPublicAuthRoute(pathname: string): boolean {
  return PUBLIC_AUTH_ROUTES.some((prefix) =>
    prefix.endsWith("/")
      ? pathname.startsWith(prefix)
      : pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function isMfaRoute(pathname: string): boolean {
  return pathname === "/mfa" || pathname.startsWith("/mfa/");
}

// MFA still requires the authenticated provider stack for organization-scoped
// requirement and trusted-device state. It is suppressed from onboarding but
// deliberately does not belong to the provider-bypass route set above.
export function isWelcomeFlowSuppressedRoute(pathname: string): boolean {
  return isPublicAuthRoute(pathname) || isMfaRoute(pathname);
}

/**
 * Mount the providers required by authenticated route guards. The optional
 * tenant check makes nested legacy wrappers no-ops once the route boundary has
 * already supplied the shared provider stack.
 */
export function AuthenticatedProviders({ children }: { children: ReactNode }) {
  const existingTenantContext = useOptionalTenantContext();
  if (existingTenantContext) return <>{children}</>;

  return (
    <TenantContextProvider>
      <SupportModeProvider>
        <SolomonProvider>
          <OrganizationProvider>
            <FeatureFlagProvider>
              <SessionTimeoutGuard />
              <GlobalHotkeys />
              <SupportModeBanner />
              {children}
            </FeatureFlagProvider>
          </OrganizationProvider>
        </SolomonProvider>
      </SupportModeProvider>
    </TenantContextProvider>
  );
}

export function AuthenticatedRouteProviders({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  if (isPublicAuthRoute(pathname)) return <>{children}</>;
  return <AuthenticatedProviders>{children}</AuthenticatedProviders>;
}
